// Sync Responsiveness: selector refreshFn callbacks are throttled so the
// Vue-reactive mirror is assigned at most once per MIRROR_INTERVAL_MS. Each
// assignment copies the whole selection array and Vue then walks the copy to
// make it reactive, so the cost is O(selection) every time.
//
// This was previously rafDebounce — once per animation frame. That only
// coalesces when individuals arrive faster than frames. During a bulk sync
// they arrive at roughly one per frame, so it collapsed almost nothing:
// ~28,000 full copies of an array growing towards 35,000 entries. Measured on
// a 23,742-twin instance, that cache-size-proportional cost was about two
// thirds of an eight-minute cold sync.
//
// The internal selector _selection array is still updated immediately, so
// queries always see current data; only the Vue mirror lags, by at most one
// interval. A flush on CacheSyncCompleteEvent guarantees the final state is
// published without waiting for the trailing timer.
//
// Applied to: index, geoCoord, type selectors. NOT applied to: tree (has its
// own debounce), exception (needs synchronous cascade reads from cache within
// the same callback).
// See docs/sync-responsiveness.md for details.

import Vue, { markRaw } from 'vue'

import { RecCache } from '@/rec-cache/rec-cache'
import { WobNamespaces } from '@/wob-client/wob-client-namespaces'
import {
  WobUpAggregators,
  WobDownAggregators,
} from '@/vue-wob-cache/vue-wob-aggregators'
import { geoCoordinateParser } from '@/wob-client/wob-client-geo-coordinate-parser'
import {
  AdtUpTreeEdges,
  AdtDownTreeEdges,
  AdtSuperRoots,
} from '@/wob-client/adt-tree-edges'
// TODO refactor/remove after testing...
import { /* Alarms, */ ExceptionEvent } from './vue-wob-alarms'
import { Iris } from '@/rec-cache/rec-cache-types'
import { CacheSyncCompleteEvent } from '@/rec-cache/rec-cache-events'

import { Ontology } from '@/wob-ontology-services/ontology'
import { ConstraintsDB } from '@/wob-ontology-services/constraints'
import { setOntology } from '@/vue-wob-cache/vue-wob-icons'

/**
 * Freeze a published tree so Vue leaves it alone.
 *
 * Assigned to a reactive property, a plain tree is walked and every node made
 * reactive — ~45k on the Fabege graph. Worse, v-treeview's search filter is a
 * computed that reads every node, so it ends up depending on all of them, and
 * each of the ~1,100 root rows reading it inherits that whole dependency list:
 * typing one letter blocked the page for seconds. Nothing needs the nodes to
 * be reactive — the selection replaces the tree wholesale on every rebuild and
 * never edits one in place, and Vue skips frozen objects entirely.
 */
function freezeTree(nodes) {
  for (const node of nodes) {
    if (Object.isFrozen(node)) continue
    if (node.children) freezeTree(node.children)
    Object.freeze(node)
  }
  return Object.freeze(nodes)
}

// Coalesces rapid-fire calls into a single update per animation frame.
// Prevents O(n²) Vue reactivity overhead during bulk sync operations.
// Vue mirrors are refreshed at most this often during bulk ingest. One
// second keeps the UI visibly progressing while cutting the number of
// full-array copies by two orders of magnitude versus per-frame.
const MIRROR_INTERVAL_MS = 1000

// Trailing throttle: coalesces calls into at most one invocation per `ms`,
// always with the most recent arguments. `flush()` publishes immediately.
function throttle(fn, ms) {
  let timer = null
  let latestArgs = null
  let last = 0
  const invoke = () => {
    timer = null
    last = Date.now()
    const args = latestArgs
    latestArgs = null
    if (args) fn(...args)
  }
  const wrapped = function (...args) {
    latestArgs = args
    if (timer) return
    timer = setTimeout(invoke, Math.max(0, ms - (Date.now() - last)))
  }
  wrapped.flush = () => {
    if (!timer) return
    clearTimeout(timer)
    invoke()
  }
  return wrapped
}

export default {
  async install(vue, options = {}) {
    console.log('vue-wob-cache.vue-wob-cache.install')

    // The plugin no longer constructs the client or the auth strategy. The
    // bootstrap layer (main.js) decides which backend + auth pair to wire
    // and passes pre-built instances in.
    //
    // - profile          : the active connectionProfile (kind='adt'|'pcx', etc.)
    // - authForProfile   : (profile) => Auth   — rebuild the strategy for a
    //                      profile change (settings dialog, profile switch)
    // - applyProfile     : (client, profile) => void — push profile changes
    //                      into the existing client (origin/host updates).
    //                      Backend-specific; main.js owns it.
    const {
      client: wobClient,
      auth: initialAuth,
      profile: initialProfile,
      authForProfile,
      applyProfile,
    } = options

    if (
      !wobClient ||
      !initialAuth ||
      !initialProfile ||
      !authForProfile ||
      !applyProfile
    ) {
      throw new Error(
        'vue-wob-cache: install requires { client, auth, profile, authForProfile, applyProfile }'
      )
    }
    console.log(
      'vue-wob-cache.vue-wob-cache.install.connection-profile',
      initialProfile
    )

    // Build asset path type categories from ontology for the AssetPathSelector.
    // Each category is a Set of fully expanded type IRI values.
    const ontology = new Ontology()
    const assetPathTypeCategories = (() => {
      const iri = (v) => ontology.iri(v) || { value: v }
      const subValues = (type) =>
        ontology
          .subclasses(iri(type))
          .map((c) => c.value)
          .concat(type)
      return {
        realestateTypes: new Set(subValues(Iris.recRealEstate)),
        buildingTypes: new Set(subValues(Iris.recBuilding)),
        levelTypes: new Set(
          subValues(Iris.recLevel).concat(subValues(Iris.recSubBuilding))
        ),
        roomTypes: new Set(subValues(Iris.recRoom)),
        // All Space subclasses — used as a broad filter when walking includes edges
        // so that equipment locatedIn any space subclass (building, level, room, etc.) is recognized
        spaceTypes: new Set(subValues(Iris.recSpace)),
        systemTypes: new Set(subValues(Iris.brickSystem)),
        equipmentTypes: new Set(
          subValues(Iris.brickEquipment).concat(
            subValues(Iris.brickSensorEquipment)
          )
        ),
        pointTypes: new Set(subValues(Iris.brickPoint)),
      }
    })()

    const recCache = new RecCache({
      namespaces: WobNamespaces,
      upAggregators: WobUpAggregators,
      downAggregators: WobDownAggregators,
      client: wobClient,
      geoCoordinateFn: geoCoordinateParser,
      upTreeEdges: AdtUpTreeEdges,
      downTreeEdges: AdtDownTreeEdges,
      assetPathTypeCategories,
    })

    recCache.index = {}
    recCache.geoCoords = {}
    recCache.types = {}
    recCache.tree = {}
    recCache.assetPaths = {}

    // Alarm and exception application. Written in front end for now.
    // TODO refactor/remove after testing...
    // recCache.alarms = {}
    // recCache.exceptions = {}
    // recCache.impacts = {}

    // Variables for second iteration of alarms and exceptions application.
    recCache.exceptionEvents = {}
    recCache.openExceptions = {}
    recCache.activeAlarms = {}
    recCache.alarmingAssets = {}
    recCache.impactedAssets = {}

    // Keep Vue out of the tree and asset path selectors. `recCache` goes into
    // `data` below, and Vue walks everything reachable from it, class
    // instances included: selector -> its selections -> the selection's tree.
    // Every node a rebuild pushed onto that tree was made reactive on the
    // spot, before the bridge ever froze it, so freezing at publish kept the
    // getters instead of preventing them. v-treeview's search filter then
    // depended on all ~43k nodes, and every rendered row copied that list
    // whenever it evaluated its children: 3-4s per keystroke and per expand
    // on the Fabege graph. No view reads selector state reactively; what
    // views need is published explicitly below (tree.nodes, isRebuilding,
    // hasEverBuilt, assetPaths.rows).
    markRaw(recCache.treeSelector)
    if (recCache.assetPathSelector) markRaw(recCache.assetPathSelector)

    const reactiveCache = new Vue({
      data() {
        return {
          recCache,
        }
      },

      async created() {
        // TODO add try catch
        console.log(
          'vue-wob-cache.vue-wob-cache.install.reactive-cache.created'
        )

        // If running inside an MSAL popup window, skip all initialization.
        // The popup only needs to complete the auth handshake and close itself.
        if (window.opener && window.opener !== window) {
          console.log(
            'vue-wob-cache: Running inside MSAL popup, skipping initialization'
          )
          return
        }

        // const auth0 = await createAuth0Client({
        //   domain: 'webofbuildings.eu.auth0.com',
        //   clientId: '4suSZAqIRYd0qiPdW8IqwAQ1rqD3yg3Q',
        // })
        // Object.defineProperty(this.recCache, 'auth0', { value: auth0 })

        await initialAuth.initialize()
        const auth = initialAuth

        console.log(
          'vue-wob-cache.vue-wob-cache.install.reactive-cache.created.auth-client',
          auth
        )

        Object.defineProperty(this.recCache, 'msal', {
          value: auth,
          writable: true,
        })

        // Token refresh callback used by the client before each request. The
        // strategy handles silent → popup fallback internally.
        this.recCache.client.tokenProvider = () => auth.getToken()

        const accessToken = await auth.getToken()
        console.log(
          'vue-wob-cache.vue-wob-cache.install.reactive-cache.created.is-authenticated',
          !!accessToken
        )
        if (accessToken) {
          const account = auth.getAccount()
          console.log(
            'vue-wob-cache.vue-wob-cache.install.reactive-cache.created.user-account',
            account
          )

          this.recCache.client.setSession({
            token: accessToken,
            user: account,
            isAuthenticated: true,
          })

          // Initialize user-specific IndexedDB before starting sync
          // Uses MSAL homeAccountId as unique identifier (format: "localAccountId.tenantId")
          // NOTE: If switching to different auth provider, use their stable user identifier
          await this.recCache.initializeWithUser(account.homeAccountId)

          // Check for and recover from any incomplete change-type transactions
          await this.recCache.recoverPendingTransactions()

          // Start sync - RecCache will use IDB cache if initialized, otherwise loads from API only
          this.recCache.startSync()
        } else {
          // Anonymous/unauthenticated - no IDB caching, no sync
          console.log(
            'vue-wob-cache: Anonymous mode, sync and IndexedDB caching disabled'
          )
        }
      },
    })

    // Expose the connection profile on the rec cache so the UI can read it
    // (settings dialog, header pills, etc.).
    Object.defineProperty(reactiveCache.recCache, 'connectionProfile', {
      value: initialProfile,
      writable: true,
    })

    Object.defineProperty(reactiveCache.recCache, 'updateProfile', {
      value: async function (profile) {
        // TODO add try-catch...

        console.log(
          'vue-wob-cache.vue-wob-cache.install.reactive-cache.methods.update-profile',
          profile
        )

        // Push profile-derived transport changes (origin/host) into the
        // existing client. Backend-specific; main.js owns the implementation.
        applyProfile(this.client, profile)

        // Build a fresh auth strategy from the new profile via the factory
        // injected at install time, and rewire the client's tokenProvider.
        const auth = authForProfile(profile)
        await auth.initialize()
        this.msal = auth
        this.client.tokenProvider = () => auth.getToken()

        // Persist the profile and update the in-memory copy.
        localStorage.setItem('connectionProfile', JSON.stringify(profile))
        this.connectionProfile = profile

        console.log(
          'vue-wob-cache.vue-wob-cache.install.reactive-cache.created.auth-client',
          auth
        )
        // TODO should a silent signin be attempted?
      },
    })

    // The login and logout functions are defined as properties on the REC cache.
    Object.defineProperty(reactiveCache.recCache, 'login', {
      value: async function () {
        try {
          console.log(
            'vue-wob-cache.vue-wob-cache.install.reactive-cache.methods.login'
          )

          const { token, account } = await this.msal.login()
          console.log(
            'vue-wob-cache.vue-wob-cache.install.reactive-cache.methods.login.token',
            // Presence only — never the credential itself.
            token ? '<redacted>' : null
          )

          this.client.setSession({
            token,
            user: account,
            isAuthenticated: true,
          })

          // Clear old cache data and close any existing IDB connection.
          this.clear()

          // Initialize user-specific IndexedDB for the newly logged-in user
          // Uses MSAL homeAccountId as unique identifier (format: "localAccountId.tenantId")
          // NOTE: If switching to different auth provider, use their stable user identifier
          await this.initializeWithUser(account.homeAccountId)

          // Go through startSync rather than calling the client directly: it
          // waits for hydration, reconciles change detection against what
          // hydration produced, and passes isCacheHydrated. Calling
          // readAllRequest() bare here discarded change detection on every
          // login, so a full re-parse ran even when IDB had just restored the
          // whole cache.
          await this.startSync()
        } catch (error) {
          console.log(
            'vue-wob-cache.vue-wob-cache.install.reactive-cache.methods.login.error',
            error
          )
        }
      },
    })

    Object.defineProperty(reactiveCache.recCache, 'logout', {
      // TODO add try-catch
      value: async function () {
        try {
          console.log(
            'vue-wob-cache.vue-wob-cache.install.reactive-cache.methods.logout'
          )

          // Description of logout methods https://learn.microsoft.com/en-us/entra/identity-platform/scenario-spa-sign-in?tabs=javascript2#sign-out-behavior-on-browsers
          // Using logoutRedirect throws errors when trying to login again, so
          // the strategy uses logoutPopup with a mainWindowRedirectUri.
          const homeAccountId = this.client.user?.homeAccountId
          this.msal.logout({ homeAccountId })

          this.client.clearSession()
          this.clear()
          this.client.clearEtagMap()
          // No need to wait for IDB since we just cleared everything
          this.client.readAllRequest()
        } catch (error) {
          console.log(
            'vue-wob-cache.vue-wob-cache.install.reactive-cache.methods.logout.error',
            error
          )
        }
      },
    })

    // console.log('vue-wob-cache', reactiveCache)

    Vue.set(reactiveCache.recCache.index, 'ids', [])
    Vue.set(reactiveCache.recCache.geoCoords, 'ids', [])
    Vue.set(reactiveCache.recCache.types, 'ids', [])
    Vue.set(reactiveCache.recCache.tree, 'nodes', [])
    // Reactive, unlike the isBuilt/isBuilding getters below: a plain getter
    // gives Vue nothing to track, so a view bound to it would never re-render
    // when it changed.
    Vue.set(reactiveCache.recCache.tree, 'isRebuilding', false)
    Vue.set(reactiveCache.recCache.tree, 'hasEverBuilt', false)
    Vue.set(reactiveCache.recCache.assetPaths, 'isRebuilding', false)
    Vue.set(reactiveCache.recCache.assetPaths, 'rows', [])

    // TODO refactor/remove after testing...
    // Vue.set(reactiveCache.recCache.alarms, 'ids', [])
    // Vue.set(reactiveCache.recCache.exceptions, 'ids', [])
    // Vue.set(reactiveCache.recCache.impacts, 'ids', [])

    // Selector lists for alarms and exceptions.
    // All comprises arrays of IRIs, Iri[]
    Vue.set(reactiveCache.recCache.exceptionEvents, 'ids', [])
    Vue.set(reactiveCache.recCache.openExceptions, 'ids', [])
    Vue.set(reactiveCache.recCache.activeAlarms, 'ids', [])
    Vue.set(reactiveCache.recCache.alarmingAssets, 'ids', [])
    Vue.set(reactiveCache.recCache.impactedAssets, 'ids', [])

    // console.log('vue-wob-cache.set', reactiveCache)

    // new Test(reactiveCache.recCache.index.ids)

    const indexMirror = throttle((ids) => {
      reactiveCache.recCache.index.ids = [...ids]
    }, MIRROR_INTERVAL_MS)
    const indexSelection = recCache.indexSelector.createSelection(indexMirror)
    // A query is somebody waiting on the answer. The mirror's throttle exists
    // to coalesce the stream of updates during a sync; left in the path of a
    // query it can hold a fresh result back for up to MIRROR_INTERVAL_MS while
    // a sync is running. Publish query results immediately.
    reactiveCache.recCache.index.query = (text) => {
      const selection = indexSelection.query(text)
      indexMirror.flush()
      return selection
    }
    Object.defineProperty(reactiveCache.recCache.index, 'size', {
      get() {
        return this.ids.length
      },
    })

    const geoCoordMirror = throttle((ids) => {
      reactiveCache.recCache.geoCoords.ids = [...ids]
    }, MIRROR_INTERVAL_MS)
    const geoCoordSelection =
      recCache.geoCoordSelector.createSelection(geoCoordMirror)
    reactiveCache.recCache.geoCoords.query =
      geoCoordSelection.query.bind(geoCoordSelection)
    Object.defineProperty(reactiveCache.recCache.geoCoords, 'size', {
      get() {
        return this.ids.length
      },
    })

    const typeMirror = throttle((ids) => {
      reactiveCache.recCache.types.ids = [...ids]
    }, MIRROR_INTERVAL_MS)
    const typeSelection = recCache.typeSelector.createSelection(typeMirror)
    // Publish the final selection state as soon as ingest stops, rather than
    // leaving the last update sitting on the trailing timer.
    addEventListener(CacheSyncCompleteEvent, () => {
      indexMirror.flush()
      geoCoordMirror.flush()
      typeMirror.flush()
      assetPathMirror.flush()
    })
    // Same reasoning as index.query above: a type-filter change is a user
    // action, not ingest, and should not wait on the throttle.
    reactiveCache.recCache.types.query = (types) => {
      const selection = typeSelection.query(types)
      typeMirror.flush()
      return selection
    }
    Object.defineProperty(reactiveCache.recCache.types, 'list', {
      get() {
        return reactiveCache.recCache.typeSet
      },
    })

    // Tree selector setup
    const treeSelection = recCache.treeSelector.createSelection(
      (tree) => {
        reactiveCache.recCache.tree.nodes = freezeTree(tree)
        // Set on publish rather than read through a getter: the selection
        // is kept out of Vue (see markRaw above), so a getter would give a
        // view nothing to track.
        reactiveCache.recCache.tree.hasEverBuilt = treeSelection.hasEverBuilt
      },
      { superRoots: AdtSuperRoots }
    )
    reactiveCache.recCache.tree.query =
      treeSelection.rebuild.bind(treeSelection)
    // Views register interest here (see the `observes` mixin). Nothing
    // rebuilds the tree while no view is observing it.
    reactiveCache.recCache.tree.observe =
      treeSelection.observe.bind(treeSelection)
    treeSelection.onRunningChange = (running) => {
      reactiveCache.recCache.tree.isRebuilding = running
    }
    reactiveCache.recCache.tree.findNode =
      treeSelection.findNode.bind(treeSelection)
    reactiveCache.recCache.tree.getPath =
      treeSelection.getPath.bind(treeSelection)
    Object.defineProperty(reactiveCache.recCache.tree, 'size', {
      get() {
        return this.nodes.length
      },
    })
    Object.defineProperty(reactiveCache.recCache.tree, 'isBuilt', {
      get() {
        return treeSelection.isBuilt
      },
    })
    Object.defineProperty(reactiveCache.recCache.tree, 'isBuilding', {
      get() {
        return treeSelection.isBuilding
      },
    })

    // Asset path selector setup
    const assetPathMirror = throttle((rows) => {
      reactiveCache.recCache.assetPaths.rows = rows
    }, MIRROR_INTERVAL_MS)
    const assetPathSelection = recCache.assetPathSelector
      ? recCache.assetPathSelector.createSelection(assetPathMirror)
      : null
    // Backends without an asset path selector still answer observe(), so a
    // view can register interest without knowing which backend it is on.
    reactiveCache.recCache.assetPaths.observe = () => () => {}
    if (assetPathSelection) {
      reactiveCache.recCache.assetPaths.rebuild =
        assetPathSelection.rebuild.bind(assetPathSelection)
      reactiveCache.recCache.assetPaths.observe =
        assetPathSelection.observe.bind(assetPathSelection)
      assetPathSelection.onRunningChange = (running) => {
        reactiveCache.recCache.assetPaths.isRebuilding = running
      }
      Object.defineProperty(reactiveCache.recCache.assetPaths, 'size', {
        get() {
          return this.rows.length
        },
      })
      Object.defineProperty(reactiveCache.recCache.assetPaths, 'isBuilt', {
        get() {
          return assetPathSelection.isBuilt
        },
      })
    }

    // Auto-rebuild control
    // Enable from start to allow progressive tree building during initial sync
    let autoRebuildEnabled = true

    // Private: Refresh all Vue selections from cached data
    // Called internally after IndexedDB hydration - not exposed publicly
    const refreshAllSelections = () => {
      console.log('📋 Refreshing all Vue selections from cache...')
      // Trigger index selection with null query to get all IDs
      indexSelection.query(null)
      // Trigger geoCoord selection with null query to get all geo IDs
      geoCoordSelection.query(null)
      // Trigger type selection with null/empty query to get all types
      typeSelection.query([])
      // Request rather than force: hydration finishing is not by itself a
      // reason to spend seconds rebuilding a view nobody has opened.
      treeSelection.requestRebuild()
      if (assetPathSelection) assetPathSelection.requestRebuild()
      console.log('📋 All selections refreshed')
    }

    // Trigger background resync - exposed for UI "Resync" button
    // Keeps current data visible while syncing in background
    // Note: Call the method via the prototype to avoid recursion since we're defining it on the same object
    Object.defineProperty(reactiveCache.recCache, 'resync', {
      value: function () {
        RecCache.prototype.resync.call(recCache)
      },
    })

    // Build tree and populate selections when IndexedDB cache is hydrated (before API sync completes)
    // This makes all views available immediately from cached data
    addEventListener('rec-cache-hydration-complete', () => {
      console.log(
        '🌲 Cache: IndexedDB hydration complete - refreshing all selections from cached data'
      )
      // Note: updateRoots() is already called in RecCache._initializeIndexedDB()
      refreshAllSelections()
    })

    // Trigger final rebuild when sync completes to catch any updates/deletions.
    // rebuildEdges() re-scans all individuals to self-heal edge maps that may
    // be stale from IDB hydration or ETag-skipped syncs.
    addEventListener('rec-cache-sync-complete', () => {
      console.log('🌲 API sync complete - rebuilding edges from scratch')
      recCache.rebuildEdges()
      recCache.treeSelector.rebuildEdges()
      treeSelection.requestRebuild()
      if (recCache.assetPathSelector) {
        recCache.assetPathSelector.rebuildEquipmentIds()
        if (assetPathSelection) assetPathSelection.requestRebuild()
      }
    })

    // Report that something changed and let each selection decide what that
    // is worth. The debounce that used to live here could not do that job:
    // it only knew how long ago the last change arrived, not whether anyone
    // was looking or what a rebuild costs on a graph this size.
    const scheduleTreeRebuild = () => {
      if (!autoRebuildEnabled) return
      treeSelection.requestRebuild()
      if (assetPathSelection) assetPathSelection.requestRebuild()
    }

    // Auto-rebuild on updates (debounced)
    addEventListener('rec-cache-update', scheduleTreeRebuild)

    // Auto-rebuild on deletes (debounced)
    addEventListener('rec-cache-delete', scheduleTreeRebuild)

    // TODO being refactored. Remove when new implememtation is finalized.
    // const alarmSelection = recCache.typeSelector.createSelection((ids) => {
    //   reactiveCache.recCache.alarms.ids = [...ids]
    //   console.log(
    //     'vue-wob-cache.vue-wob-cache.install.alarms-callback.alarms',
    //     ids
    //   )

    //   const alarms = ids.map((a) => reactiveCache.recCache.read(a))
    //   const exceptions = alarms
    //     .map((a) => a.getValuesByName('https://w3id.org/rec#isPointOf'))
    //     .filter((values) => !!values)
    //     .reduce((prev, curr) => prev.concat(curr), [])
    //     .map((value) => value.value)
    //   reactiveCache.recCache.exceptions.ids = [...exceptions]

    //   // Recursive function to traverse all feeds relationships from an equipment.
    //   // Parameter ids is an array of Iris.
    //   // Returns array of all impacted individuals as id Iris.
    //   function impactsThroughFeeds(ids) {
    //     // TODO will eventually try to read individual that is not cached yet, leading to error.
    //     // For testing purposes undefined reads are filtered out.
    //     const equipments = ids
    //       .map((id) => reactiveCache.recCache.read(id))
    //       .filter((e) => !!e)
    //     console.log('EQUIPMENT', equipments)
    //     const feeds = equipments
    //       .map((e) => e.getValuesByName('https://w3id.org/rec#feeds'))
    //       .filter((values) => !!values)
    //       .reduce((prev, curr) => prev.concat(curr), [])
    //     if (feeds.length) {
    //       return equipments.map((e) => e.id).concat(impactsThroughFeeds(feeds))
    //     } else {
    //       return equipments.map((e) => e.id)
    //     }
    //   }
    //   // TODO the impacts will include the exceptions.
    //   // Refactor to only traverse layers after exceptions (equipment with alarms).
    //   reactiveCache.recCache.impacts.ids = impactsThroughFeeds(exceptions).map(
    //     (value) => value.value
    //   )
    // })
    // // Set a permanent query for the alarms that are monitored.
    // alarmSelection.query(Alarms)

    // Creates a callback for changes in the exception events.
    // Parameter ids is an array of iris, Iri[]
    // Used to update the list of assets directly and indirectly (through propagation) affected by an exception event.
    const exceptionSelection = recCache.typeSelector.createSelection((ids) => {
      reactiveCache.recCache.exceptionEvents.ids = [...ids]
      console.log(
        'vue-wob-cache.vue-wob-cache.install.events-callback.exception-events',
        ids
      )

      // Identify open exception events, ie exception events with no end time.
      // TODO the defined filter is necessary to cater for not yet read individuals.
      const openExceptions = ids
        .map((e) => reactiveCache.recCache.read(e))
        .filter((e) => !!e)
        .filter(
          (e) =>
            !e.getValuesByName(Iris.recEnd) ||
            !e.getValuesByName(Iris.recEnd).length
        )
      // TODO the mapping to ids may be embedded above but then the console cannot output. Refactor when tested.
      reactiveCache.recCache.openExceptions.ids = openExceptions.map(
        (e) => e.id
      )
      console.log(
        'vue-wob-cache.vue-wob-cache.install.events-callback.open-exception',
        openExceptions
      )

      // Identify active alarms, ie where end time for exception event has not been set.
      const activeAlarms = reactiveCache.recCache.openExceptions.ids
        .map((id) =>
          reactiveCache.recCache.queryEdges({
            target: id.value,
            label: 'https://w3id.org/rec#lastKnownValue',
          })
        )
        .filter((edge) => !!edge)
        .reduce((prev, curr) => prev.concat(curr), [])
        .map((edge) => edge.source)
      console.log(
        'vue-wob-cache.vue-wob-cache.install.events-callback.active-alarms',
        activeAlarms
      )
      reactiveCache.recCache.activeAlarms.ids = activeAlarms

      // Identify alarming assets, ie assets with active alarms. Array of IRIs, Iri[]
      const alarmingAssets = activeAlarms
        .map((a) => reactiveCache.recCache.read(a))
        .filter((a) => !!a)
        // TODO move property literal to Iris...
        .map((a) => a.getValuesByName('https://w3id.org/rec#isPointOf'))
        .filter((values) => !!values)
        .reduce((prev, curr) => prev.concat(curr), [])
      reactiveCache.recCache.alarmingAssets.ids = [...alarmingAssets]
      console.log(
        'vue-wob-cache.vue-wob-cache.install.events-callback.alarming-assets',
        alarmingAssets
      )

      // Recursive function to traverse all feeds relationships from an equipment.
      // Parameter ids is an array of IRIs, Iri[]
      // Returns array of all impacted equipment individuals as id Iris, Iri[]
      function impactedThroughFeeds(ids) {
        // TODO will eventually try to read individual that is not cached yet, leading to error.
        // For testing purposes undefined reads are filtered out.
        const equipments = ids
          .map((id) => reactiveCache.recCache.read(id))
          .filter((e) => !!e)
        const feeds = equipments
          // TODO move property literal to Iris...
          .map((e) => e.getValuesByName('https://w3id.org/rec#feeds'))
          .filter((values) => !!values)
          .reduce((prev, curr) => prev.concat(curr), [])
        if (feeds.length) {
          return equipments.map((e) => e.id).concat(impactedThroughFeeds(feeds))
        } else {
          return equipments.map((e) => e.id)
        }
      }

      reactiveCache.recCache.impactedAssets.ids =
        impactedThroughFeeds(alarmingAssets)
      console.log(
        'vue-wob-cache.vue-wob-cache.install.events-callback.impacted-assets',
        reactiveCache.recCache.impactedAssets.ids
      )
    })
    exceptionSelection.query([ExceptionEvent])

    console.log(
      'vue-wob-cache.vue-wob-cache.types',
      reactiveCache.recCache.types
    )

    vue.prototype.$wob = reactiveCache.recCache

    // TODO likely better for the module to export an ontology... or at a later stage be able to somehow point out which to use
    vue.prototype.$wobo = ontology
    setOntology(vue.prototype.$wobo)

    // The
    const constraintsDB = new ConstraintsDB(reactiveCache.recCache.namespaces)
    console.log('VUE WOB CACHE CONSTRAINTS', constraintsDB)
    vue.prototype.$constraints = constraintsDB

    // console.log(
    //   'vue-wob-cache',
    //   Array.isArray(reactiveCache.recCache.index.ids)
    // )
  },
}
